/**
 * 규칙 겹침 분석 — "이 규칙이 실제로 나오기는 하나".
 *
 * 엔진의 승부는 결정적이다(우선순위 내림차순 → 규칙 id 오름차순). 문제는 그 결과가
 * 편집기에서 안 보인다는 것이다. 우선순위를 둘 다 0 으로 두면 **먼저 만든 규칙이 이기고**,
 * 나중에 만든 규칙은 한 번도 나오지 않는다. 맞는 동작이지만 화면에서는 알 길이 없어,
 * 운영자는 "왜 이 편성이 안 나오지" 를 엉뚱한 데서 찾는다.
 *
 * 가장 나쁜 경우는 **완전히 가려진 규칙**이다 — 저장은 되고 목록에도 보이는데 영원히
 * 재생되지 않는다. 그것만은 저장 전에 말해 줘야 한다.
 */
import { compareRules, matchesDaypart, parseHhmm, type ScheduleRuleRef } from '@signageone/core';
import type { ScheduleRule } from '../api/client.js';

/** 한 주를 훑는 간격(분). 규칙 시각은 분 단위라 5분이면 경계를 놓치지 않는다. */
const STEP_MINUTES = 5;

export interface RuleOverlap {
  /** 이 규칙이 매칭되는 시간(분/주). 0 이면 요일이 비었거나 시각이 잘못됐다. */
  matchedMinutes: number;
  /** 그중 실제로 이기는 시간. */
  winningMinutes: number;
  /** 자기를 가리는 다른 규칙 id — 이긴 쪽만. */
  shadowedBy: string[];
}

function toRef(rule: ScheduleRule): ScheduleRuleRef {
  return {
    id: rule.id,
    label: rule.label,
    days: rule.days,
    start: rule.start,
    end: rule.end,
    playlistId: rule.playlist_id,
    assetId: rule.asset_id,
    priority: rule.priority,
  };
}

/**
 * 규칙마다 "매칭되는 시간"과 "실제로 이기는 시간"을 센다.
 *
 * 한 주(7일)를 훑는다. 심야 구간이 요일 경계를 넘나들기 때문에 하루만 봐서는 안 된다 —
 * 월요일 22:00~02:00 은 화요일 새벽에도 살아 있다.
 */
/**
 * 재생할 것을 고른 규칙만 — 분석과 그림은 이것만 본다(없는 편성을 따지지 않는다).
 *
 * 플레이리스트만 보던 때가 있었다. 콘텐츠 하나를 직접 트는 규칙이 생긴 뒤에도 그대로라,
 * **콘텐츠 규칙은 다른 규칙에 통째로 가려져도 "재생 안 됨" 이 뜨지 않았다.**
 */
function hasTarget(rule: ScheduleRule): boolean {
  return Boolean(rule.playlist_id || rule.asset_id);
}

export function analyzeOverlaps(rules: readonly ScheduleRule[]): Map<string, RuleOverlap> {
  const usable = rules.filter(hasTarget);
  const refs = usable.map(toRef);
  const result = new Map<string, RuleOverlap>();
  const shadowSets = new Map<string, Set<string>>();

  for (const rule of usable) {
    result.set(rule.id, { matchedMinutes: 0, winningMinutes: 0, shadowedBy: [] });
    shadowSets.set(rule.id, new Set());
  }

  for (let day = 0; day < 7; day += 1) {
    for (let minutes = 0; minutes < 1440; minutes += STEP_MINUTES) {
      const fields = { day, minutes };
      const active = refs.filter((ref) =>
        matchesDaypart({ days: ref.days, start: ref.start, end: ref.end }, fields),
      );
      if (active.length === 0) continue;

      const winner = [...active].sort(compareRules)[0] as ScheduleRuleRef;
      for (const ref of active) {
        const entry = result.get(ref.id);
        if (!entry) continue;
        entry.matchedMinutes += STEP_MINUTES;
        if (ref.id === winner.id) entry.winningMinutes += STEP_MINUTES;
        else shadowSets.get(ref.id)?.add(winner.id);
      }
    }
  }

  for (const [id, shadows] of shadowSets) {
    const entry = result.get(id);
    if (entry) entry.shadowedBy = [...shadows].sort();
  }

  return result;
}

export type OverlapVerdict = 'ok' | 'partial' | 'hidden';

/**
 * 한 규칙의 판정.
 *
 * `hidden` 은 저장하면 안 되는 상태에 가깝다 — 목록에는 보이는데 영원히 재생되지 않는다.
 * `partial` 은 정상일 수 있다: 넓은 규칙 위에 좁은 규칙을 얹는 것이 데이파팅의 정석이다.
 */
export function verdictFor(overlap: RuleOverlap | undefined): OverlapVerdict {
  if (!overlap || overlap.matchedMinutes === 0) return 'ok';
  if (overlap.winningMinutes === 0) return 'hidden';
  return overlap.shadowedBy.length > 0 ? 'partial' : 'ok';
}

/** 규칙을 부를 이름. 이름이 없으면 시간대로 대신한다 — id 는 사람이 읽을 것이 아니다. */
export function ruleTitle(rule: ScheduleRule | undefined): string {
  if (!rule) return '다른 규칙';
  return rule.label.trim() || `${rule.start}~${rule.end}`;
}

// ─────────────────────────── 요일 ───────────────────────────

/** 한 규칙이 걸리는 요일과 실제로 나오는 요일. 0(일) ~ 6(토). */
export interface RuleDays {
  /** 요일 설정상 걸리는 요일. 자정을 넘는 규칙은 다음 날(그 새벽)도 들어간다. */
  matched: number[];
  /** 그중 실제로 이겨서 화면에 나오는 요일. 그날 다른 규칙에 통째로 가려지면 빠진다. */
  playing: number[];
}

/**
 * 규칙마다 어느 요일에 나오는가 — 오른쪽 하루 그림의 요일 칩이 이것으로 표시를 단다.
 *
 * 규칙에는 요일이 적혀 있지만 **적힌 요일과 나오는 요일은 다르다.** 자정을 넘는 규칙
 * (금 22:00~02:00)은 토요일 새벽에도 나오고, 우선순위에 진 규칙은 적힌 요일에도 안 나온다.
 * 그래서 겹침 분석과 같은 방식(엔진의 판정 함수)으로 한 주를 훑어 센다.
 *
 * 훑는 간격은 5분이고, 규칙마다 시작 분을 더 본다 — 5분보다 짧은 규칙(16:12~16:15)이
 * 격자 사이에 숨어 "나오는 요일 없음" 이 되지 않게.
 */
export function ruleWeekdays(rules: readonly ScheduleRule[]): { byRule: Map<string, RuleDays>; playing: number[] } {
  const usable = rules.filter(hasTarget);
  const refs = usable.map(toRef);
  const matched = new Map<string, Set<number>>();
  const playing = new Map<string, Set<number>>();
  for (const rule of usable) {
    matched.set(rule.id, new Set());
    playing.set(rule.id, new Set());
  }

  const samples = new Set<number>();
  for (let minutes = 0; minutes < 1440; minutes += STEP_MINUTES) samples.add(minutes);
  for (const rule of usable) {
    const start = parseHhmm(rule.start);
    if (start !== null && start < 1440) samples.add(start);
  }

  const anyDay = new Set<number>();
  for (let day = 0; day < 7; day += 1) {
    for (const minutes of samples) {
      const fields = { day, minutes };
      const active = refs.filter((ref) => matchesDaypart({ days: ref.days, start: ref.start, end: ref.end }, fields));
      if (active.length === 0) continue;
      const winner = [...active].sort(compareRules)[0] as ScheduleRuleRef;
      for (const ref of active) matched.get(ref.id)?.add(day);
      playing.get(winner.id)?.add(day);
      anyDay.add(day);
    }
  }

  const sorted = (set: Set<number> | undefined): number[] => [...(set ?? [])].sort((a, b) => a - b);
  const byRule = new Map<string, RuleDays>();
  for (const rule of usable) {
    byRule.set(rule.id, { matched: sorted(matched.get(rule.id)), playing: sorted(playing.get(rule.id)) });
  }
  return { byRule, playing: sorted(anyDay) };
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 요일 묶음을 사람이 읽는 말로 — 평일·주말·매일은 그 이름으로, 나머지는 월요일부터 늘어놓는다.
 *
 * 월요일부터 세는 이유: 달력은 일요일부터지만 "월·수·금" 처럼 말할 때는 월요일부터 센다.
 */
export function weekdayList(days: readonly number[]): string {
  const set = new Set(days);
  if (set.size === 7) return '매일';
  if (set.size === 5 && [1, 2, 3, 4, 5].every((d) => set.has(d))) return '평일';
  if (set.size === 2 && set.has(0) && set.has(6)) return '주말';
  return [1, 2, 3, 4, 5, 6, 0]
    .filter((d) => set.has(d))
    .map((d) => DAY_NAMES[d])
    .join('·');
}

/**
 * `from` 에서 가장 가까운 앞날(오늘 포함) 중 `days` 에 든 요일. 없으면 `from`.
 *
 * 고른 규칙이 지금 보는 요일에 없을 때 미리보기를 옮길 자리다 — 뒤로 가지 않는 이유는
 * "다음에 언제 나오나" 가 운영자가 묻는 말이기 때문이다.
 */
export function nearestWeekday(from: number, days: readonly number[]): number {
  for (let step = 0; step < 7; step += 1) {
    const day = (from + step) % 7;
    if (days.includes(day)) return day;
  }
  return from;
}
